import {
  INormalizedStanding,
  INormalizedStandings,
  IStageResponse,
  IStanding,
  IStandingsConfig,
  ITournamentGroup,
} from '../../../../packages/types';
import { normalizeKnockoutData } from '@sportal365/smp-widget-normalizers';
import { INormalizedFootballStageResponseData } from '../types';
import { normalizeStandingData } from './normalizeStandingData';
import { StageTypes } from '../../../../packages/constants/stageType';
import { generateFootballStandingsColumnsHeader } from '../helpers/standings';

interface INormalizeStandingsArgs {
  standing?: IStanding[];
  dataEntityLinks?: IStandingsConfig['dataEntityLinks'];
  dataTeamNameType?: IStandingsConfig['dataTeamNameType'];
  dataLabels?: IStandingsConfig['dataLabels'];
  dataElements: Array<string>;
  displayImage: boolean;
  isMobile: boolean;
  dataHighlightEntities?: IStandingsConfig['dataHighlightEntities'];
}

interface INormalizeFootballStageResponseData {
  stage?: IStageResponse;
  dataLabels?: IStandingsConfig['dataLabels'];
  dataEntityLinks?: IStandingsConfig['dataEntityLinks'];
  dataTeamNameType?: IStandingsConfig['dataTeamNameType'];
  dataElements?: IStandingsConfig['dataElementsDesktop'] | IStandingsConfig['dataElementsMobile'];
  displayImage?: IStandingsConfig['dataImageDisplay'];
  isMobile?: boolean;
  dataHighlightEntities?: IStandingsConfig['dataHighlightEntities'];
}

const normalizeStandings = ({
  standing,
  dataEntityLinks,
  dataTeamNameType,
  dataLabels,
  dataElements,
  displayImage,
  isMobile,
  dataHighlightEntities,
}: INormalizeStandingsArgs): INormalizedStandings => {
  const normalizedStandings = standing?.reduce<INormalizedStanding[]>((output, standing) => {
    const normalizedStanding = normalizeStandingData({
      standing,
      dataEntityLinks,
      dataTeamNameType,
      dataElements,
      displayImage,
      isMobile,
      dataHighlightEntities,
    });
    if (normalizedStanding) output.push(normalizedStanding);

    return output;
  }, []);

  const { desktopColumns, mobileColumns } = generateFootballStandingsColumnsHeader(dataLabels, dataElements);

  return {
    data: normalizedStandings,
    desktopColumns,
    mobileColumns,
  };
};

export const normalizeFootballStageResponseData = ({
  stage,
  dataLabels,
  dataEntityLinks,
  dataTeamNameType,
  dataElements = [],
  displayImage = true,
  isMobile = false,
  dataHighlightEntities,
}: INormalizeFootballStageResponseData): INormalizedFootballStageResponseData | undefined => {
  if (!stage || !stage?.data || !stage?.type) return;

  if (stage.type === StageTypes.STANDINGS) {
    const normalizedStandings = normalizeStandings({
      standing: stage.data,
      dataEntityLinks,
      dataTeamNameType,
      dataLabels,
      dataElements,
      displayImage: displayImage,
      isMobile,
      dataHighlightEntities,
    });

    return {
      standings: [
        {
          item: normalizedStandings,
        },
      ],
    };
  }
  if (stage.type === StageTypes.GROUPS) {
    const normalizedGroupStanding = stage.data.map(({ name, standing }: ITournamentGroup) => ({
      name: `${dataLabels?.group || 'GROUP'} ${name}`,
      item: normalizeStandings({
        standing,
        dataLabels,
        dataEntityLinks,
        dataTeamNameType,
        dataElements,
        displayImage: displayImage,
        isMobile,
        dataHighlightEntities,
      }),
    }));
    return {
      standings: normalizedGroupStanding,
    };
  }

  if (stage.type === StageTypes.KNOCK_OUT) {
    return {
      knockout: normalizeKnockoutData({ knockout: stage.data, dataEntityLinks, dataTeamNameType }),
    };
  }
};
